import {
  Alert,
  Button,
  Card,
  Input,
  Modal,
  Popconfirm,
  Segmented,
  Space,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import useApi from "../../utils/useApi";
import { describeError, formatDateTime } from "../ai-reports/status";
import { comContexto, diffDeLinhas } from "./textos";

const { Text } = Typography;
const { TextArea } = Input;
const FONTE_MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

const ESTILO_DA_LINHA = {
  igual: { color: "inherit" },
  saiu: { background: "rgba(220, 38, 38, 0.12)", color: "#b91c1c" },
  entrou: { background: "rgba(22, 163, 74, 0.12)", color: "#15803d" },
  omitidas: { color: "#9ca3af", fontStyle: "italic" },
};
const SINAL = { igual: " ", saiu: "-", entrou: "+" };

function Diff({ antes, depois }) {
  const linhas = diffDeLinhas(antes, depois);
  if (!linhas.some((linha) => linha.tipo !== "igual")) {
    return <Text type="secondary">Nenhuma diferença.</Text>;
  }
  return (
    <pre
      style={{
        fontFamily: FONTE_MONO,
        fontSize: 12,
        maxHeight: "60vh",
        overflow: "auto",
        margin: 0,
        whiteSpace: "pre-wrap",
      }}>
      {comContexto(linhas).map((linha, indice) => (
        <div key={indice} style={ESTILO_DA_LINHA[linha.tipo]}>
          {linha.tipo === "omitidas"
            ? `  ··· ${linha.quantidade} linha(s) sem mudança ···`
            : `${SINAL[linha.tipo]} ${linha.texto}`}
        </div>
      ))}
    </pre>
  );
}

function Avisos({ avisos }) {
  if (!avisos?.length) return null;
  return (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 12 }}
      message="Pontos que o texto não cobre"
      description={
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {avisos.map((aviso) => (
            <li key={aviso}>{aviso}</li>
          ))}
        </ul>
      }
    />
  );
}

function VersaoModal({ numero, ativo, onFechar, onReativar }) {
  const { getAgentPromptVersion } = useApi();
  const [modo, setModo] = useState("diff");
  const { data, isFetching } = useQuery({
    queryKey: ["agent-prompt-version", numero],
    queryFn: () => getAgentPromptVersion(numero),
    enabled: numero !== null,
  });

  return (
    <Modal
      open={numero !== null}
      width={960}
      title={data ? `Versão ${data.numero}` : "Versão"}
      onCancel={onFechar}
      footer={
        data && !data.ativo ? (
          <Popconfirm
            title={`Reativar a versão ${data.numero}?`}
            description="Vale a partir da próxima resposta do agente."
            okText="Reativar"
            cancelText="Cancelar"
            onConfirm={() => onReativar(data.numero)}>
            <Button type="primary">Reativar esta versão</Button>
          </Popconfirm>
        ) : null
      }>
      {isFetching && !data ? (
        <Text type="secondary">Carregando…</Text>
      ) : data ? (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text type="secondary">
            {data.nota || "Sem nota"} · {data.created_by || "sistema"} ·{" "}
            {formatDateTime(data.created_at)}
          </Text>
          {!data.ativo && (
            <Segmented
              value={modo}
              onChange={setModo}
              options={[
                { value: "diff", label: "Diferença para a ativa" },
                { value: "texto", label: "Texto completo" },
              ]}
            />
          )}
          {data.ativo || modo === "texto" ? (
            <pre
              style={{
                fontFamily: FONTE_MONO,
                fontSize: 12,
                maxHeight: "60vh",
                overflow: "auto",
                whiteSpace: "pre-wrap",
                margin: 0,
              }}>
              {data.texto}
            </pre>
          ) : (
            <Diff antes={data.texto} depois={ativo?.texto} />
          )}
        </Space>
      ) : null}
    </Modal>
  );
}

export default function PromptCard() {
  const {
    getAgentPrompt,
    createAgentPromptVersion,
    activateAgentPromptVersion,
  } = useApi();
  const queryClient = useQueryClient();
  const [texto, setTexto] = useState("");
  const [nota, setNota] = useState("");
  const [revisando, setRevisando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [versaoAberta, setVersaoAberta] = useState(null);

  const { data, isFetching } = useQuery({
    queryKey: ["agent-prompt"],
    queryFn: getAgentPrompt,
    staleTime: 0,
  });
  const ativo = data?.ativo;

  useEffect(() => {
    if (ativo) setTexto(ativo.texto);
  }, [ativo]);

  const alterado = Boolean(ativo) && texto.trim() !== ativo.texto.trim();

  const recarregar = () => {
    queryClient.invalidateQueries({ queryKey: ["agent-prompt"] });
    queryClient.invalidateQueries({ queryKey: ["agent-prompt-version"] });
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      const criada = await createAgentPromptVersion({ texto, nota });
      notification.success({
        message: `Versão ${criada.numero} ativa.`,
        description: "Vale a partir da próxima resposta do agente.",
      });
      if (criada.avisos?.length) {
        notification.warning({
          message: "Salvo, mas confira o texto",
          description: criada.avisos.join(" "),
          duration: 10,
        });
      }
      setRevisando(false);
      setNota("");
      recarregar();
    } catch (err) {
      notification.error({
        message: "Erro ao salvar o prompt.",
        description: describeError(err, ""),
      });
    } finally {
      setSalvando(false);
    }
  };

  const reativar = async (numero) => {
    try {
      await activateAgentPromptVersion(numero);
      notification.success({ message: `Versão ${numero} reativada.` });
      setVersaoAberta(null);
      recarregar();
    } catch (err) {
      notification.error({
        message: "Erro ao reativar a versão.",
        description: describeError(err, ""),
      });
    }
  };

  const colunas = [
    {
      title: "Versão",
      dataIndex: "numero",
      render: (numero, versao) => (
        <Space size={6}>
          <Text>{numero}</Text>
          {versao.ativo && <Tag color="success">Ativa</Tag>}
        </Space>
      ),
    },
    { title: "Nota", dataIndex: "nota", render: (valor) => valor || "-" },
    { title: "Autor", dataIndex: "created_by", render: (v) => v || "sistema" },
    {
      title: "Criada em",
      dataIndex: "created_at",
      render: (valor) => formatDateTime(valor),
    },
    {
      title: "",
      key: "acoes",
      align: "right",
      render: (_, versao) => (
        <Button size="small" onClick={() => setVersaoAberta(versao.numero)}>
          Ver
        </Button>
      ),
    },
  ];

  return (
    <Card
      title="Prompt do agente"
      extra={
        ativo && (
          <Text type="secondary">
            Ativa: versão {ativo.numero} ({ativo.rotulo})
          </Text>
        )
      }>
      <Avisos avisos={ativo?.avisos} />
      <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
        Instruções fixas do agente. Os dados da conversa (cliente, filial,
        papel, canal e confirmação pendente) entram sozinhos no bloco
        &lt;contexto&gt;. Salvar cria uma versão nova, que vale a partir da
        próxima resposta.
      </Text>
      <TextArea
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        autoSize={{ minRows: 12, maxRows: 30 }}
        disabled={isFetching && !data}
        style={{ fontFamily: FONTE_MONO, fontSize: 12 }}
      />
      <Space style={{ marginTop: 12 }}>
        <Button
          type="primary"
          disabled={!alterado}
          onClick={() => setRevisando(true)}>
          Revisar e salvar nova versão
        </Button>
        <Button disabled={!alterado} onClick={() => setTexto(ativo.texto)}>
          Descartar alterações
        </Button>
        <Text type="secondary">{texto.length} caracteres</Text>
      </Space>
      <Text strong style={{ display: "block", margin: "24px 0 12px" }}>
        Histórico
      </Text>
      <Table
        size="small"
        rowKey="numero"
        loading={isFetching}
        columns={colunas}
        dataSource={data?.versoes || []}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
      />
      <Modal
        open={revisando}
        width={960}
        title="Salvar nova versão do prompt"
        okText="Salvar e ativar"
        cancelText="Voltar"
        confirmLoading={salvando}
        onOk={salvar}
        onCancel={() => setRevisando(false)}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Input
            placeholder="O que mudou? (ex.: respostas mais curtas no WhatsApp)"
            value={nota}
            maxLength={200}
            onChange={(e) => setNota(e.target.value)}
          />
          <Text type="secondary">Diferença para a versão ativa:</Text>
          <Diff antes={ativo?.texto} depois={texto} />
        </Space>
      </Modal>
      <VersaoModal
        numero={versaoAberta}
        ativo={ativo}
        onFechar={() => setVersaoAberta(null)}
        onReativar={reativar}
      />
    </Card>
  );
}
